import React, { FC } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker.css"; // Import custom styles
import { format } from 'date-fns';

interface InputProps {
  type?: "text" | "number" | "email" | "password" | "date" | "time" | string;
  id?: string;
  name?: string;
  placeholder?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  disabled?: boolean;
  success?: boolean;
  error?: boolean;
  hint?: string; // Optional hint text
  dateFormat?: string; // For DatePicker
  showTimeSelect?: boolean; // For DatePicker
  timeFormat?: string; // For DatePicker
  onDateChange?: (date: Date | null) => void; // For DatePicker
  autoComplete?: string; // Para controlar el autocompletado
}

const Input: FC<InputProps> = ({
  type = "text",
  id,
  name,
  placeholder,
  value,
  onChange,
  onFocus,
  className = "",
  min,
  max,
  step,
  disabled = false,
  success = false,
  error = false,
  hint,
  dateFormat = "yyyy-MM-dd",
  showTimeSelect = false,
  timeFormat = "HH:mm",
  onDateChange,
  autoComplete,
}) => {
  // Determine input styles based on state (disabled, success, error)
  let inputClasses = `h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 ${className}`;

  // Add styles for the different states
  if (disabled) {
    inputClasses += ` text-gray-500 border-gray-300 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
  } else if (error) {
    inputClasses += ` text-error-800 border-error-500 focus:ring focus:ring-error-500/10  dark:text-error-400 dark:border-error-500`;
  } else if (success) {
    inputClasses += ` text-success-500 border-success-400 focus:ring-success-500/10 focus:border-success-300  dark:text-success-400 dark:border-success-500`;
  } else {
    inputClasses += ` bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800`;
  }

  // Handle DatePicker if type is date
  if (type === "date") {
    // Parse value to Date object (if it's a string)
    let dateValue: Date | null = null;
    if (value && typeof value === 'string') {
      const parsedDate = new Date(value);
      if (!isNaN(parsedDate.getTime())) {
        dateValue = parsedDate;
      }
    }

    // Handle date change
    const handleDateChange = (date: Date | null) => {
      if (onDateChange) {
        onDateChange(date);
      } else if (onChange && date) {
        // Format the date as YYYY-MM-DD string for standard HTML input compatibility
        const formattedDate = format(date, 'yyyy-MM-dd');
        const syntheticEvent = {
          target: {
            name,
            value: formattedDate
          }
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    return (
      <div className="relative">
        <DatePicker
          id={id}
          name={name}
          selected={dateValue}
          onChange={handleDateChange}
          onFocus={onFocus as any}
          className={inputClasses}
          placeholderText={placeholder}
          disabled={disabled}
          dateFormat={dateFormat}
          showTimeSelect={showTimeSelect}
          timeFormat={timeFormat}
          minDate={min ? new Date(min.toString()) : undefined}
          maxDate={max ? new Date(max.toString()) : undefined}
          autoComplete={autoComplete}
        />
        {/* Optional Hint Text */}
        {hint && (
          <p
            className={`mt-1.5 text-xs ${
              error
                ? "text-error-500"
                : success
                ? "text-success-500"
                : "text-gray-500"
            }`}
          >
            {hint}
          </p>
        )}
      </div>
    );
  }

  // For non-date input types, return the original Input component
  return (
    <div className="relative">
      <input
        type={type}
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className={inputClasses}
        autoComplete={autoComplete}
      />

      {/* Optional Hint Text */}
      {hint && (
        <p
          className={`mt-1.5 text-xs ${
            error
              ? "text-error-500"
              : success
              ? "text-success-500"
              : "text-gray-500"
          }`}
        >
          {hint}
        </p>
      )}
    </div>
  );
};

export default Input;
